import React, { useMemo, useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { images } from '../data/mock';
import { Mail, Phone, User, CheckCircle, Sparkles, PartyPopper, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stable across renders (react-hooks/purity)
const NEWSLETTER_PARTICLES = Array.from({ length: 25 }, (_, i) => ({
  key: i,
  cls: i % 3 === 0 ? 'bg-pink-400/40' : i % 3 === 1 ? 'bg-orange-400/40' : 'bg-yellow-400/40',
  style: {
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    animationDelay: `${Math.random() * 5}s`,
    animationDuration: `${10 + Math.random() * 10}s`,
  }
}));

const NewsletterSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      
      if (!response.ok) throw new Error('Failed to subscribe');
      
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '' });
      if (typeof window !== 'undefined' && window.__atfTrackLead) {
        window.__atfTrackLead('newsletter_signup', { form_id: 'newsletter-section' });
      }
    } catch (err) {
      setError('Subscription failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 sm:py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src={images.hawaiianSunset}
          alt="Hawaiian sunset"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />
      </div>

      {/* Floating particles - hidden on mobile for performance */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none hidden sm:block">
        {NEWSLETTER_PARTICLES.map((p) => (
          <div
            key={p.key}
            className={`absolute w-3 h-3 rounded-full animate-float ${p.cls}`}
            style={p.style}
          />
        ))}
      </div>

      <div className="max-w-4xl mx-auto px-4 relative z-10">
        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 sm:p-8 md:p-12 border border-pink-500/20 hover-lift">
          {submitted ? (
            <div className="text-center py-8 sm:py-12 animate-bounceIn">
              <div className="flex justify-center mb-4 sm:mb-6">
                <div className="relative">
                  <CheckCircle className="w-16 h-16 sm:w-24 sm:h-24 text-green-400" />
                  <PartyPopper className="w-8 h-8 sm:w-10 sm:h-10 text-pink-400 absolute -top-2 -right-2 animate-bounce" />
                </div>
              </div>
              <h3 className="text-2xl sm:text-4xl font-black text-white mb-3 sm:mb-4">You&apos;re In!</h3>
              <p className="text-white/80 text-base sm:text-xl">Get ready for exclusive deals and early access!</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-flex items-center gap-2 mb-3 sm:mb-4">
                  <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-pink-400 animate-pulse" />
                  <span className="text-pink-400 font-bold tracking-wider text-xs sm:text-sm">VIP ACCESS</span>
                  <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400 animate-pulse" />
                </div>
                <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-white mb-3 sm:mb-4">
                  Get Exclusive Deals!
                </h2>
                <p className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-orange-400 text-sm sm:text-lg font-semibold px-2">
                  VIP access starts here. Don&apos;t miss the best pours, best deals, and the best times!
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="relative group">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 group-focus-within:text-pink-400 transition-colors" />
                  <Input
                    type="text"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-14 pl-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 focus:ring-pink-500/20 rounded-xl text-lg"
                    required
                  />
                </div>

                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 group-focus-within:text-pink-400 transition-colors" />
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-14 pl-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 focus:ring-pink-500/20 rounded-xl text-lg"
                    required
                  />
                </div>

                <div className="relative group">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 group-focus-within:text-pink-400 transition-colors" />
                  <Input
                    type="tel"
                    placeholder="Phone (optional)"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="h-14 pl-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 focus:ring-pink-500/20 rounded-xl text-lg"
                  />
                </div>

                {error && (
                  <p className="text-red-400 text-sm text-center">{error}</p>
                )}

                <Button 
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="w-full h-14 sm:h-16 bg-gradient-to-r from-pink-500 via-orange-500 to-yellow-500 hover:from-pink-600 hover:via-orange-600 hover:to-yellow-600 text-white text-lg sm:text-xl font-bold rounded-xl shadow-xl hover:shadow-pink-500/40 transition-all duration-300 transform hover:scale-[1.02] animate-gradient group disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  ) : (
                    <span className="flex items-center justify-center gap-3">
                      <PartyPopper className="w-6 h-6 group-hover:animate-bounce" />
                      Join the Party!
                      <PartyPopper className="w-6 h-6 group-hover:animate-bounce" />
                    </span>
                  )}
                </Button>
              </form>

              <p className="text-center text-white/50 text-sm mt-6">
                We respect your privacy. Unsubscribe anytime.
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;
